import { useMemo, useState } from 'react'
import type { AnswerMap, AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'

interface DosageTemplateProps {
  stage: WorkflowStage
  answers: AnswerMap
  currentPhase?: PhaseInfo | null
  onSelect: (value: AnswerValue) => void
}

const GLP1_MEDICATIONS = [
  { id: 'ozempic', label: 'Ozempic® (Semaglutide)' },
  { id: 'wegovy', label: 'Wegovy® (Semaglutide)' },
  { id: 'mounjaro', label: 'Mounjaro® (Tirzepatide)' },
  { id: 'zepbound', label: 'Zepbound® (Tirzepatide)' },
  { id: 'compounded_semaglutide', label: 'Compounded Semaglutide [CarniSema®]' },
  { id: 'compounded_tirzepatide', label: 'Compounded Tirzepatide [CarniTide®]' },
  { id: 'other', label: 'Other / Not sure' },
]

const DOSAGE_OPTIONS = {
  semaglutide: ['0.25 mg', '0.5 mg', '1 mg', '1.7 mg', '2.4 mg', 'Not sure'],
  tirzepatide: ['2.5 mg', '5 mg', '7.5 mg', '10 mg', '12.5 mg', '15 mg', 'Not sure'],
  compounded: ['Low dose', 'Medium dose', 'High dose', 'Not sure'],
} as const

function getDosageType(medicationId: string): keyof typeof DOSAGE_OPTIONS {
  if (['ozempic', 'wegovy', 'compounded_semaglutide'].includes(medicationId)) return 'semaglutide'
  if (['mounjaro', 'zepbound', 'compounded_tirzepatide'].includes(medicationId)) return 'tirzepatide'
  return 'compounded'
}

export default function DosageTemplate({ answers, currentPhase, onSelect }: DosageTemplateProps) {
  const meds = useMemo(
    () =>
      Array.isArray(answers.current_glp1_medications)
        ? (answers.current_glp1_medications as string[]).filter(Boolean)
        : [],
    [answers.current_glp1_medications]
  )

  const [dosages, setDosages] = useState<Record<string, string>>(() => {
    const existing = answers.glp1_dosages
    if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
      return existing as Record<string, string>
    }
    return {}
  })

  const setDosage = (medicationId: string, dose: string) => {
    setDosages((prev) => ({ ...prev, [medicationId]: dose }))
  }

  const allDone = meds.every((medicationId) => !!dosages[medicationId])

  const handleContinue = () => {
    if (!allDone) return
    onSelect({
      glp1_dosages: dosages,
      current_glp1_dosages: dosages,
      past_glp1_dosages: dosages,
    })
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="flex flex-col gap-5">
        {meds.map((medicationId) => {
          const medInfo = GLP1_MEDICATIONS.find((medication) => medication.id === medicationId)
          const dosageType = getDosageType(medicationId)
          const options = DOSAGE_OPTIONS[dosageType] || DOSAGE_OPTIONS.compounded
          return (
            <fieldset key={medicationId} className="flex flex-col gap-2 border-none">
              <legend className="mb-1.5 text-[13px] font-medium text-white/70">
                {medInfo?.label || medicationId}
              </legend>
              <div className="flex flex-wrap gap-1.5">
                {options.map((dose) => (
                  <button
                    key={dose}
                    type="button"
                    className={`rounded-xl border px-4 py-3 text-[13px] font-medium transition-all duration-300 ${
                      dosages[medicationId] === dose
                        ? 'border-[var(--ww-accent)] choice-selected text-[var(--ww-text)] selected-glow'
                        : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)]'
                    }`}
                    onClick={() => setDosage(medicationId, dose)}
                  >
                    {dose}
                  </button>
                ))}
              </div>
            </fieldset>
          )
        })}
      </div>

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button
          type="button"
          disabled={!allDone}
          onClick={handleContinue}
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
